// Bottle engraving for brands and events. Targets: perfume bottle engraving
// 1,900, bottle engraving 1,600, champagne bottle engraving 1,000, liquor
// bottle engraving 880, wine bottle engraving 880, bottle engraving nyc 50.
// Inbox: a 200-bottle brand job ($4.1K quoted) and champagne for a PR agency.
// The studio doesn't sell alcohol; clients supply the bottles.
const {
  quoted,
  setup,
  each,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'bottleEngraving',
  slug: 'bottle-engraving',
  path: '/services/laser-engraving/bottle-engraving/',
  noun: 'bottle',
  crumb: 'Bottle engraving',
  title: 'Bottle Engraving: Wine, Champagne & Liquor | PrintAWorld',
  description:
    'Laser bottle engraving in NYC for wine, champagne, liquor and perfume bottles: logos for brand launches, names for events and gifts. One or hundreds.',
  eyebrow: 'Bottle engraving / New York studio',
  heading:
    'Bottle engraving for wine, champagne, liquor and perfume, for brands and events.',
  answer: `PrintAWorld laser-engraves bottles in New York: wine, champagne, liquor and perfume bottles with a brand logo for a launch or PR mailer, a guest’s name for an event, or a message for a gift. The laser frosts the glass, or cuts through a painted bottle’s coating. You supply the bottles; engraving is a ${setup} setup plus ${each(small, 'bottle')}, and two-sided or color work is ${quoted.toLowerCase()}.`,
  cta: 'Get a bottle quote',
  heroImage: 'champagne',
  ideaImages: ['eventBottles', 'perfume', 'wineBox'],
  images: {
    champagne: {
      file: 'champagne-logo',
      alt: 'Dark green champagne bottle with a frosted engraved logo and 2026, beside two coupes on a marble bar',
      caption: 'A logo frosted into champagne glass',
    },
    eventBottles: {
      file: 'event-bottles',
      alt: 'Row of liquor bottles each engraved with a different guest name under the same event logo',
      caption: 'A name on every guest’s bottle',
    },
    perfume: {
      file: 'perfume-monogram',
      alt: 'Faceted perfume bottle with a small frosted engraved monogram and a gold cap',
      caption: 'A monogram on a perfume bottle',
    },
    wineBox: {
      file: 'wine-gift-box',
      alt: 'Wine bottle engraved with a winery name, a couple’s names and a date, in an engraved wooden box',
      caption: 'Wine and an engraved box',
    },
  },
  intro: {
    eyebrow: 'For launches, PR and parties',
    title: 'Custom engraved bottles with your logo or a name.',
    paragraphs: [
      'A frosted logo on a champagne bottle reads as part of the bottle, not a sticker on it. Brands send them to press and creators, hosts give them to VIP guests, and companies send them to clients.',
      'Send one bottle to test. Bottle shape, glass color and coating all change the look, so we engrave a sample before the run.',
    ],
  },
  ideas: {
    eyebrow: 'Bottle ideas',
    title: 'Engraved champagne, liquor, wine and perfume bottles.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Custom bottles for brands, events and gifts.',
    items: [
      {
        label: 'Brand launches',
        title: 'PR mailers and seeding',
        body: 'Your logo or campaign line on bottles for press, creators and partners.',
      },
      {
        label: 'Events',
        title: 'A name for every guest',
        body: 'VIP tables, bottle service, galas and dinners, each bottle with its guest’s name.',
      },
      {
        label: 'Corporate gifts',
        title: 'Client and team gifts',
        body: 'A bottle with the company logo and the year, for closings, holidays and milestones.',
      },
      {
        label: 'Beauty brands',
        title: 'Perfume and cosmetic bottles',
        body: 'Monograms and names on fragrance bottles for launches and in-store personalization.',
      },
      {
        label: 'Weddings',
        title: 'Names and the date',
        body: 'Champagne for the toast, wine for the parents, a bottle for every member of the party.',
      },
    ],
  },
  materials: {
    link: 'Which bottles',
    eyebrow: 'Which bottles',
    title: 'Glass, painted and coated bottles.',
    items: [
      {
        title: 'Clear and colored glass',
        body: 'Wine, liquor and perfume glass engraves to a frosted white mark that can’t rub off.',
      },
      {
        title: 'Painted bottles',
        body: 'On painted or coated bottles, the laser removes the coating to show the glass. We test the coating first.',
      },
      {
        title: 'Labels and foil',
        body: 'We engrave around labels and foil, or on the back. Tell us what must stay untouched.',
      },
      {
        title: 'Boxes and cases',
        body: 'Engraved wooden boxes and cases for the bottle, made on the same lasers.',
      },
    ],
  },
  process: {
    title: 'From your logo to a case of engraved bottles.',
    note: 'Standard lead time is 2–3 weeks after the bottles reach us. Send a spare bottle for the test.',
    steps: [
      {
        title: 'Send the design and bottle',
        body: 'Your logo or the names, the bottle type, how many, and the date.',
      },
      {
        title: 'Approve a test bottle',
        body: 'We engrave a spare and send you a photo of the real result.',
      },
      {
        title: 'We engrave the run',
        body: 'Each bottle is engraved in the same place and checked, with names matched to your list.',
      },
      {
        title: 'Packed for the event',
        body: 'Back in their cases, picked up in NYC or couriered to your venue.',
      },
    ],
  },
  pricing: {
    title: 'Bottle engraving pricing, per bottle.',
    cta: 'Price my bottles',
    rows: [
      { label: 'Engraving, per bottle', value: small },
      { label: 'A second side or a wrap', value: quoted },
      { label: 'Color fill or UV printing', value: quoted },
      { label: 'Wooden boxes', value: quoted },
      { label: 'Rush for an event', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Bottles in bulk',
    title: 'Two hundred bottles for a launch, engraved the same.',
    paragraphs: [
      'For runs we engrave and approve one bottle, then the rest on the same setup. Names come from your spreadsheet and are checked bottle by bottle.',
      'Bottles can be delivered or couriered to the studio in New York by your distributor.',
    ],
    cta: 'Plan a bottle order',
  },
  faqTitle: 'Bottle engraving questions, answered.',
  faqs: [
    {
      question: 'How much does bottle engraving cost?',
      answer: costAnswer(
        small,
        `Two sides, a wrap, color fill and wooden boxes are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'Do you supply the bottles?',
      answer:
        'No. We don’t sell alcohol or perfume. You or your distributor send the bottles; we engrave them and send them back.',
    },
    {
      question: 'What does engraved glass look like?',
      answer:
        'A frosted white mark in the glass. There is no color unless we fill it or UV print on the bottle, which we quote separately.',
    },
    {
      question: 'Can you engrave painted champagne bottles?',
      answer:
        'Usually. The laser removes the paint to show the glass underneath. Coatings differ, so we test one bottle first.',
    },
    {
      question: 'Can each bottle have a different name?',
      answer:
        'Yes. Send the names in a spreadsheet; we keep the logo the same and engrave a name on each bottle.',
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('bottle'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer(
        'For an event, tell us the date and send the bottles early.'
      ),
    },
    {
      question: 'Do you ship?',
      answer: `${shipAnswer} Carriers have rules for shipping alcohol, so pickup or a courier is often easier.`,
    },
  ],
  quote: {
    title: 'Tell us about your bottles.',
    body: 'Send the logo or names, the bottle type, how many and the date. We’ll reply with a test plan and a written quote.',
    checklist: [
      'Your logo or the names list',
      'The bottle, with a photo or a link',
      'How many, and where the engraving goes',
      'Your event or in-hand date',
    ],
  },
  moreCards: ['corporateGifts', 'tumblers', 'cuttingBoards'],
  extraRelated: [
    ['On-site event engraving', '/services/on-site-laser-engraving-nyc/'],
  ],
  schema: {
    name: 'Bottle Engraving',
    alternateName: [
      'Champagne bottle engraving',
      'Liquor bottle engraving',
      'Wine bottle engraving',
      'Perfume bottle engraving',
    ],
    serviceType: 'Laser engraving on glass bottles',
  },
})
